import { ReactElement, useCallback, useState } from 'react'
import { Add, BorderStyle, PlayArrow } from '@mui/icons-material'
import { Box } from '@mui/material'
import usePlanImageCardTool from '@editor/hooks/usePlanImageCardTool'
import usePlanTableTool from '@editor/hooks/usePlanTableTool'
import { CursorIcon, ImportCursorIcon } from '@ui2/icons'
import { ToggleButton } from '@ui/actions'
import { useControlStore, useEditElementStore } from '@editorStores'
import { useIfcMode, useIfcModelMetadata, useIfcSelection } from '@editorHooks'
import {
  Toolbar as EditorToolbar,
  ToolbarGroup,
  StoreyControls,
  CommentToolSelect,
  PlanTableToolSelect,
  PlanImageCardToolSelect,
  BuildingToolSelect,
  IFCFileSelect,
  useAddCommentTool,
} from '@editorComponents'
import useIFCImportBuildingMode from '../../hooks/useIFCImportBuildingMode'
import useIFCImportGuided from '../../hooks/useIFCImportGuided'
import useIFCImportUpload from '../../hooks/useIFCImportUpload'
import GuidedStartDialog from '../GuidedStartDialog'
import IFCDrawToolSelect, { IFCDrawMode } from './IFCDrawToolSelect'
import SettingsToolSelect, { IFCFileToolMode } from './SettingsToolSelect'

const DRAW_MODES: IFCDrawMode[] = ['draw-roofs', 'draw-walls']

const Toolbar = (): ReactElement => {
  const { isActive: isAddCommentActive, toggle: toggleAddComment } = useAddCommentTool()
  const { isActive: isAddPlanTableActive, toggle: toggleAddPlanTable } = usePlanTableTool()
  const { isActive: isAddPlanImageCardActive, toggle: toggleAddPlanImageCard } =
    usePlanImageCardTool()

  const { isBuildingActive, toggleBuilding } = useIFCImportBuildingMode()

  const { isAddActive, toggleAdd } = useIFCImportUpload()

  const { isGuidedActive, startGuided, toggleGuided } = useIFCImportGuided()

  // When a file is already selected, starting the guided import asks whether to use it or upload a
  // new one; otherwise the workflow starts straight away (with the upload step first).
  const [startDialogOpen, setStartDialogOpen] = useState(false)

  const { ifcMode, setIfcMode } = useIfcMode()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const hasModelMetadata = useIfcModelMetadata(activeIfcFileGuid) != null
  const fileToolValue: IFCFileToolMode | undefined =
    ifcMode === 'settings' ||
    ifcMode === 'alignment' ||
    ifcMode === 'filter' ||
    ifcMode === 'storey' ||
    ifcMode === 'type'
      ? ifcMode
      : undefined

  const drawToolValue = DRAW_MODES.includes(ifcMode as IFCDrawMode)
    ? (ifcMode as IFCDrawMode)
    : undefined

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const setGuidedToolbarElement = useControlStore(state => state.setIfcGuidedToolbarElement)

  const { setSelectedIfcElementGuids } = useIfcSelection()
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)
  const setMultiActiveOpenings = useEditElementStore(state => state.setMultiActiveOpenings)

  // Changing the active storey resets any current selection (both the IFC/GLB selection and the
  // planar active/multi selection) so a stale selection from another storey doesn't linger.
  const handleStoreyChange = useCallback(
    (nextStorey: string | null) => {
      setFloorplanStorey(nextStorey)
      setSelectedIfcElementGuids([])
      setActiveElement(null)
      setMultiActiveElements([])
      setMultiActiveOpenings([])
    },
    [
      setFloorplanStorey,
      setSelectedIfcElementGuids,
      setActiveElement,
      setMultiActiveElements,
      setMultiActiveOpenings,
    ],
  )

  // Which model the scene selection acts on: the IFC/GLB model or the planar (structural) model.
  const selectionMode = useControlStore(state => state.selectionMode)
  const setSelectionMode = useControlStore(state => state.setSelectionMode)

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'all' }}>
          <StoreyControls storey={storey} setStorey={handleStoreyChange} />
        </Box>
      </Box>

      {/* In guided mode the GuidedIFCDrawer portals its own toolbar into this slot. */}
      {isGuidedActive && <Box ref={setGuidedToolbarElement} />}

      {!isGuidedActive && (
        <EditorToolbar sx={{ pointerEvents: 'none' }}>
          <ToolbarGroup>
            <ToggleButton
              hint="Auswählen (a)"
              tooltipPlacement="top"
              Icon={CursorIcon}
              selected={selectionMode === 'planar-model-selection'}
              onClick={() => setSelectionMode('planar-model-selection')}
              data-cy="btn-ifc-select"
            />
            <ToggleButton
              hint="Import auswählen (a)"
              tooltipPlacement="top"
              Icon={ImportCursorIcon}
              selected={selectionMode === 'ifc-selection'}
              onClick={() => setSelectionMode('ifc-selection')}
              data-cy="btn-ifc-import-select"
            />
          </ToolbarGroup>

          <ToolbarGroup>
            <BuildingToolSelect
              isBuildingActive={isBuildingActive}
              onToggleBuilding={toggleBuilding}
            />
            <IFCDrawToolSelect
              value={drawToolValue}
              onClick={value => setIfcMode(ifcMode === value ? null : value)}
            />
            <ToggleButton
              hint="Wände automatisch zuschneiden"
              tooltipPlacement="top"
              Icon={BorderStyle}
              selected={ifcMode === 'auto-connect'}
              onClick={() => setIfcMode(ifcMode === 'auto-connect' ? null : 'auto-connect')}
              data-cy="btn-ifc-wall-auto-connect"
            />
          </ToolbarGroup>

          <ToolbarGroup>
            <CommentToolSelect
              isAddCommentActive={isAddCommentActive}
              onToggleAddComment={toggleAddComment}
            />

            <PlanTableToolSelect
              isAddPlanTableActive={isAddPlanTableActive}
              onToggleAddPlanTable={toggleAddPlanTable}
            />

            <PlanImageCardToolSelect
              isAddPlanImageCardActive={isAddPlanImageCardActive}
              onToggleAddPlanImageCard={toggleAddPlanImageCard}
            />
          </ToolbarGroup>

          <ToolbarGroup>
            <Box sx={{ pl: 1.5, pr: 1 }}>
              <IFCFileSelect hideLabel standardVariant openUpwards />
            </Box>

            <SettingsToolSelect
              value={fileToolValue}
              onSelect={mode => setIfcMode(ifcMode === mode ? null : mode)}
              disabled={!activeIfcFileGuid}
              hasModelMetadata={hasModelMetadata}
            />
            <ToggleButton
              hint="IFC-Datei hinzufügen"
              tooltipPlacement="top"
              Icon={Add}
              selected={isAddActive}
              onClick={toggleAdd}
              data-cy="btn-ifc-add"
            />
            <ToggleButton
              hint="IFC-Import starten"
              tooltipPlacement="top"
              Icon={PlayArrow}
              selected={isGuidedActive}
              onClick={() => (activeIfcFileGuid ? setStartDialogOpen(true) : toggleGuided())}
              data-cy="btn-ifc-run"
            />
          </ToolbarGroup>
        </EditorToolbar>
      )}

      <GuidedStartDialog
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        onUseCurrent={() => {
          setStartDialogOpen(false)
          startGuided(true)
        }}
        onUploadNew={() => {
          setStartDialogOpen(false)
          startGuided(false)
        }}
      />
    </>
  )
}

export default Toolbar
